"use client";

import { useState, useEffect } from "react";
import { DEFAULT_UI } from "@/lib/ui-defaults";
import {
  getFreeVhfSelfReportAnswers,
  getFreeVhfM2Answers,
  getFreeVhfM3Attempts,
  M1_SCENARIO_IDS,
  M1_SCENARIO_COUNT,
  M2_SCENARIO_IDS,
  M2_SCENARIO_COUNT,
  M3_SCENARIO_IDS,
  M3_SCENARIO_COUNT,
} from "@/lib/free-vhf-engagement-storage";

export type EngagedStatsVariant = "module1" | "module2" | "course";

export interface FreeVhfEngagedStatsStrings {
  heading?: string;
  understoodWell?: string;
  feltComfortable?: string;
  placeholder?: string;
  answerRestHint?: string;
  contextModule1?: string;
  contextModule2?: string;
  contextCourse?: string;
  /** M2 summary when variant is module2 or course (e.g. "You answered {count} of {total} Safety & Urgency calls."). */
  m2Answered?: string;
  /** M3 summary when variant is course (e.g. "You attempted {count} of {total} reactive scenarios."). */
  m3Attempted?: string;
}

export interface FreeVhfEngagedStatsProps {
  /** UI strings (from loadUIStrings(locale).freeVhf.engagedStats). */
  strings?: FreeVhfEngagedStatsStrings;
  /** Placement for optional context line. */
  variant?: EngagedStatsVariant;
  className?: string;
}

function replacePlaceholders(
  template: string,
  count: number,
  total: number
): string {
  return template
    .replace(/\{count\}/g, String(count))
    .replace(/\{total\}/g, String(total));
}

export default function FreeVhfEngagedStats({
  strings: stringsProp = {},
  variant = "course",
  className,
}: FreeVhfEngagedStatsProps) {
  const strings = {
    ...DEFAULT_UI.freeVhf?.engagedStats,
    ...stringsProp,
  };
  const [stats, setStats] = useState<{
    understoodWell: number;
    feltComfortable: number;
    total: number;
    m2Answered: number;
    m3Attempted: number;
  } | null>(null);

  useEffect(() => {
    const answers = getFreeVhfSelfReportAnswers();
    const total = M1_SCENARIO_COUNT;
    let understoodWell = 0;
    let feltComfortable = 0;
    for (const id of M1_SCENARIO_IDS) {
      const entry = answers[id];
      if (entry?.understand === "almost-everything") understoodWell += 1;
      if (entry?.comfortable === "yes") feltComfortable += 1;
    }
    const m2Answers = getFreeVhfM2Answers();
    let m2Answered = 0;
    for (const id of M2_SCENARIO_IDS) {
      const entry = m2Answers[id];
      if (entry?.who !== undefined || entry?.clear !== undefined) m2Answered += 1;
    }
    const m3Attempts = getFreeVhfM3Attempts();
    let m3Attempted = 0;
    for (const id of M3_SCENARIO_IDS) {
      if (m3Attempts[id]?.attempted) m3Attempted += 1;
    }
    const next = {
      understoodWell,
      feltComfortable,
      total,
      m2Answered,
      m3Attempted,
    };
    queueMicrotask(() => setStats(next));
  }, []);

  const fbEs = DEFAULT_UI.freeVhf?.engagedStats;
  const heading = strings.heading ?? fbEs?.heading ?? "";
  const understoodWellTpl = strings.understoodWell ?? fbEs?.understoodWell ?? "";
  const feltComfortableTpl = strings.feltComfortable ?? fbEs?.feltComfortable ?? "";
  const placeholder = strings.placeholder ?? fbEs?.placeholder ?? "";
  const answerRestHint = strings.answerRestHint ?? fbEs?.answerRestHint ?? "";

  if (stats === null) {
    return (
      <div
        className={className}
        role="region"
        aria-label={heading}
      >
        <p className="text-sm text-muted">{placeholder}</p>
      </div>
    );
  }

  const hasM1 = stats.understoodWell > 0 || stats.feltComfortable > 0;
  const hasM2 = stats.m2Answered > 0;
  const hasM3 = stats.m3Attempted > 0;
  const hasAny =
    hasM1 ||
    (variant !== "module1" && hasM2) ||
    (variant === "course" && hasM3);
  const showRestHint =
    hasM1 &&
    (stats.understoodWell < stats.total || stats.feltComfortable < stats.total);

  if (!hasAny) {
    return (
      <div
        className={className}
        role="region"
        aria-label={heading}
      >
        <p className="text-sm text-muted">{placeholder}</p>
      </div>
    );
  }

  const contextLabel =
    variant === "module1"
      ? strings.contextModule1
      : variant === "module2"
        ? strings.contextModule2
        : strings.contextCourse;

  const m2AnsweredTpl = strings.m2Answered ?? fbEs?.m2Answered ?? "";
  const m3AttemptedTpl = strings.m3Attempted ?? fbEs?.m3Attempted ?? "";
  const showM2 = (variant === "module2" || variant === "course") && hasM2 && m2AnsweredTpl;
  const showM3 = variant === "course" && hasM3 && m3AttemptedTpl;

  return (
    <div
      className={className}
      role="region"
      aria-label={heading}
    >
      {contextLabel && (
        <p className="text-sm font-medium text-content mb-1">{contextLabel}</p>
      )}
      {hasM1 && (
        <>
          <p className="text-sm text-muted">
            {replacePlaceholders(
              understoodWellTpl,
              stats.understoodWell,
              stats.total
            )}
          </p>
          <p className="text-sm text-muted">
            {replacePlaceholders(
              feltComfortableTpl,
              stats.feltComfortable,
              stats.total
            )}
          </p>
        </>
      )}
      {showM2 && (
        <p className="text-sm text-muted">
          {replacePlaceholders(
            m2AnsweredTpl,
            stats.m2Answered,
            M2_SCENARIO_COUNT
          )}
        </p>
      )}
      {showM3 && (
        <p className="text-sm text-muted">
          {replacePlaceholders(
            m3AttemptedTpl,
            stats.m3Attempted,
            M3_SCENARIO_COUNT
          )}
        </p>
      )}
      {showRestHint && (
        <p className="text-sm text-muted mt-1">{answerRestHint}</p>
      )}
    </div>
  );
}
